import * as React from "react";
import { Grid, Form, Radio, Loader, Modal, Button, Dimmer, Header, Input } from "semantic-ui-react";
import { BaseCoach, WebSettings, VideoProjectCoachConfig } from "../../../../../interfaces";
import { VideoProjectServices } from "../../../../../services";

import { modalReducer } from "./modalReducer";
import "./styles.scss";
import { useCallback, useEffect, useMemo } from "react";
import {
  AccountSettingServices,
  AccountSettingPermissionUpdate,
} from "../../../../../services/bedrock/AccountSettingServices";
import { AccountSetting, AccountSettingPermission } from "../../../../../services/bedrock/dtos/AccountSetting";

interface OwnProps {
  opened: boolean;
  coach: BaseCoach;
  handleUpdateSuccess: () => void;
}

const CoachPermissionsFC: React.FC<OwnProps> = ({ coach, opened, handleUpdateSuccess }) => {
  const [state, dispatch] = React.useReducer(modalReducer, { open: opened });

  const [loading, setLoading] = React.useState<boolean>(false);
  const [configLoading, setConfigLoading] = React.useState<boolean>(false);
  const [accountSettings, setAccountSettings] = React.useState<AccountSetting[]>([]);
  const [coachPermissions, setCoachPermissions] = React.useState<AccountSettingPermission[]>([]);
  const [permissionUpdates, setPermissionUpdates] = React.useState<AccountSettingPermissionUpdate[]>([]);
  const [defaultPlayerCredits, setDefaultPlayerCredits] = React.useState<number>();
  const [billingCycleResetDay, setBillingCycleResetDay] = React.useState<number>();
  const [vcConfig, setVCConfig] = React.useState<VideoProjectCoachConfig>();

  const isAccountSettingEnabled = useCallback(
    (accountSetting: AccountSetting) => {
      return (
        permissionUpdates.find(p => p.accountSettingId === accountSetting.id)?.value ??
        coachPermissions.find(p => p.accountSettingId === accountSetting.id)?.value ??
        accountSetting.defaultValue
      );
    },
    [permissionUpdates, coachPermissions]
  );

  const hasVC = useMemo(() => {
    const vcAccountSetting = accountSettings.find(s => s.id === WebSettings.SC_PLUS_TIER);
    return vcAccountSetting ? isAccountSettingEnabled(vcAccountSetting) : false;
  }, [isAccountSettingEnabled, accountSettings]);

  React.useEffect(() => {
    if (!coach.id) {
      setCoachPermissions([]);
      setPermissionUpdates([]);
      return;
    }

    const settingsPromise = AccountSettingServices.getAccountSettings();
    const permissionsPromise = AccountSettingServices.getAccountSettingPermissions({ coachId: Number(coach.id) });
    setLoading(true);

    Promise.all([settingsPromise, permissionsPromise])
      .then(([settingsResponse, permissionsResponse]) => {
        setAccountSettings(settingsResponse.content);
        setCoachPermissions(permissionsResponse.content);

        const newUpdates = settingsResponse.content
          .map(setting => {
            const existingPermission = permissionsResponse.content.find(p => p.accountSettingId === setting.id);
            if (!existingPermission) {
              return { accountSettingId: setting.id, value: setting.defaultValue, coachId: Number(coach.id) };
            } else {
              return null;
            }
          })
          .filter(Boolean) as AccountSettingPermissionUpdate[];
        setPermissionUpdates(newUpdates);
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    setConfigLoading(true);
    VideoProjectServices.getCoachConfig(coach.id)
      .then(response => setVCConfig(response))
      .catch(console.error)
      .finally(() => setConfigLoading(false));
  }, [coach.id]);

  useEffect(() => {
    setDefaultPlayerCredits(vcConfig?.defaultPlayerVideoCredits);
    setBillingCycleResetDay(vcConfig?.newCycleDay);
  }, [vcConfig]);

  const hasConfigChanges = useMemo(() => {
    return vcConfig?.defaultPlayerVideoCredits != defaultPlayerCredits || vcConfig?.newCycleDay != billingCycleResetDay;
  }, [vcConfig?.defaultPlayerVideoCredits, defaultPlayerCredits, vcConfig?.newCycleDay, billingCycleResetDay]);

  const handleSettingsChange = useCallback(
    (accountSettingId: string, value: boolean) => {
      const existingPermission = coachPermissions.find(p => p.accountSettingId === accountSettingId);
      const isUpdated = !existingPermission || existingPermission.value !== value;
      setPermissionUpdates(prev => {
        const newUpdates = prev.filter(p => p.accountSettingId !== accountSettingId);
        if (isUpdated) {
          newUpdates.push({ accountSettingId, value, coachId: Number(coach.id) });
        }
        return newUpdates;
      });
    },
    [coachPermissions]
  );

  const updateAdminSettings = async () => {
    setLoading(true);

    await AccountSettingServices.updateAccountSettingPermissions(permissionUpdates);
    if (hasVC && hasConfigChanges) {
      await VideoProjectServices.updateCoachConfig(coach.id, {
        defaultPlayerVideoCredits: defaultPlayerCredits,
        newCycleDay: billingCycleResetDay || 1,
      });
    }
    dispatch({ type: "close" });
    handleUpdateSuccess();
    setLoading(false);
  };
  const permissionToggles = (
    <React.Fragment>
      <Grid columns={1} padded>
        <Grid.Column>
          <Form.Group grouped key="appSettingsGroupPermissions">
            {accountSettings.map(accountSetting => {
              return (
                <Form.Group key={accountSetting.id}>
                  <Radio
                    toggle
                    label={accountSetting.name}
                    className="settingsLevelLabel"
                    onChange={(_, data) => handleSettingsChange(accountSetting.id, !!data.checked)}
                    name={accountSetting.id}
                    checked={isAccountSettingEnabled(accountSetting)}
                    disabled={loading}
                  />
                </Form.Group>
              );
            })}
          </Form.Group>
        </Grid.Column>
      </Grid>
    </React.Fragment>
  );

  const vcSettings = (
    <>
      <label>Default Player Monthly Credits</label>
      <Input
        placeholder="Default Player Monthly Credits"
        type="number"
        value={defaultPlayerCredits}
        onChange={data => {
          setDefaultPlayerCredits(data.target.valueAsNumber);
        }}
      />

      <label>Billing Cycle Reset Day</label>
      <Input
        placeholder="Billing Cycle Reset Day"
        type="number"
        value={billingCycleResetDay}
        onChange={data => {
          setBillingCycleResetDay(data.target.valueAsNumber);
        }}
      />
    </>
  );

  return (
    <Modal
      data-elm-id={`admin-dashboard-modal-coach-permission-${coach.id}`}
      open={state.open}
      onClose={() => {
        dispatch({ type: "close" });
        handleUpdateSuccess();
      }}
      closeIcon={true}
      className="coachPermissionsModal"
    >
      <Modal.Header>Coach Settings</Modal.Header>
      <Modal.Content>
        <Grid
          columns={16}
          className="adminPermissionContainer"
          verticalAlign="middle"
          stretched
          container
          textAlign="left"
        >
          <Grid.Row>
            <Grid.Column width={16}>
              <Header>Permissions</Header>
              {loading && accountSettings.length === 0 ? (
                <Dimmer active inverted>
                  <Loader size="medium">Loading</Loader>
                </Dimmer>
              ) : (
                permissionToggles
              )}
            </Grid.Column>
          </Grid.Row>
          {hasVC && (
            <Grid.Row>
              <Grid.Column width={8}>
                <Header>Video Catalyst Settings</Header>
                {configLoading ? (
                  <Dimmer active inverted>
                    <Loader size="medium">Loading</Loader>
                  </Dimmer>
                ) : (
                  vcSettings
                )}
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      </Modal.Content>

      <Modal.Actions>
        <Button
          data-elm-id="admin-dashboard-modal-coach-permission-save-btn"
          primary
          onClick={updateAdminSettings}
          content="Save"
          disabled={!hasConfigChanges && permissionUpdates.length === 0}
          loading={loading}
        />
      </Modal.Actions>
    </Modal>
  );
};

export const CoachPermissions = CoachPermissionsFC;
